// Centralized API Service for all API calls

const API_BASE_URL = 'https://leadapi.crmbytes.com/api/Lead';
const GEOLOCATION_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';

/**
 * Fetch user's geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API_URL, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return null;
  }
};

/**
 * Submit lead form data
 * @param {Object} formData - Form data object
 * @param {Object} geoData - Geolocation data object
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData, geoData = null) => {
  try {
    // Extract numeric phone number only (remove any non-numeric characters)
    const phoneNumber = formData.phone.replace(/\D/g, '');
    
    // Get country code from dropdown
    const countryDialCode = parseInt(formData.country_code.replace('+', ''));
    
    // Build payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryDialCode,
      country: getCountryCodeFromDialCode(countryDialCode),
      phoneNumber: phoneNumber, // Only numeric, no country code prefix
      ipAddress: geoData?.data?.ipAddress || '',
      description: formData.message || '',
      pageUrl: window.location.href,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    const response = await fetch(`${API_BASE_URL}/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Return response with status code
    return {
      success: response.ok && response.status === 200,
      data: data,
      statusCode: response.status
    };
  } catch (error) {
    console.error('Lead Submission Error:', error);
    return {
      success: false,
      error: error.message,
      statusCode: 500
    };
  }
};

/**
 * Get country code from dial code
 */
const getCountryCodeFromDialCode = (dialCode) => {
  const countryMap = {
    1: 'US',
    44: 'GB',
    91: 'IN',
    61: 'AU',
    49: 'DE',
    33: 'FR',
    81: 'JP',
    86: 'CN',
    82: 'KR',
    55: 'BR',
    52: 'MX',
    39: 'IT',
    34: 'ES',
    31: 'NL',
    46: 'SE',
    41: 'CH',
    65: 'SG',
    971: 'AE',
    27: 'ZA',
    64: 'NZ',
    92: 'PK'
  };
  
  return countryMap[dialCode] || 'US';
};

/**
 * Handle API response and redirect or show error
 */
export const handleLeadResponse = (response, setError) => {
  const { success, data, statusCode } = response;

  if (statusCode === 200 && data?.data?.customerId) {
    // Success - redirect to application page
    const customerId = data.data.customerId;
    window.location.href = `/application/main?customerid=${customerId}`;
    return true;
  } else if (statusCode === 401) {
    // Unauthorized
    const errorMessage = data?.message || 'Unauthorized access';
    setError(errorMessage);
    return false;
  } else if (statusCode === 400) {
    // Bad request
    const errorMessage = data?.errorMessage || 'Invalid submission';
    setError(errorMessage);
    return false;
  } else {
    // Other errors
    setError('An error occurred. Please try again.');
    return false;
  }
};
